import React, { useState, useEffect } from 'react';
import {
    Box, Container, Typography, Paper, Grid, Button, CircularProgress,
    Card, CardContent, Divider, Chip, List, ListItem, ListItemText,
    ListItemIcon, IconButton, Alert, LinearProgress, Tooltip, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Assessment as AssessmentIcon,
    History as HistoryIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Assignment as AssignmentIcon,
    TrendingUp as TrendingUpIcon,
    LocalHospital as HospitalIcon,
    Close as CloseIcon,
    Visibility as VisibilityIcon,
    TableChart as TableChartIcon,
    List as ListIcon
} from '@mui/icons-material';
import axios from 'axios';
import flaskServer from '../config/flaskserver';
import PivotTabelaProcessada from '../layout/PivotTabelaProcessada';

const CustomAudit = () => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [batchStatus, setBatchStatus] = useState('');
    const [batchProgress, setBatchProgress] = useState(0);
    const [analiseAtual, setAnaliseAtual] = useState(null);
    const [historico, setHistorico] = useState([]);
    const [convenios, setConvenios] = useState([]);
    const [selectedConvenio, setSelectedConvenio] = useState('null');
    const [error, setError] = useState('');

    // Filtros
    const [filterType, setFilterType] = useState('Todos');
    const [filterGuia, setFilterGuia] = useState('Todos');
    const [filterItem, setFilterItem] = useState('');

    const [viewMode, setViewMode] = useState('list'); // 'list' or 'pivot'

    useEffect(() => {
        carregarHistorico();
        carregarConvenios();
    }, []);

    const carregarConvenios = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${flaskServer.flaskServerIP}/convenios`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setConvenios(response.data.convenios || []);
        } catch (err) {
            console.error("Erro ao carregar convênios:", err);
        }
    };

    const carregarHistorico = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${flaskServer.flaskServerIP}/ipe/historico`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setHistorico(response.data);
        } catch (err) {
            console.error("Erro ao carregar histórico IPE:", err);
        }
    };

    const handleFileChange = (e) => {
        setFiles(Array.from(e.target.files));
        setError('');
    };

    const handleAnalisar = async () => {
        if (files.length === 0) return;
        setLoading(true);
        setError('');
        setBatchProgress(0);

        try {
            const token = localStorage.getItem('token');
            let lastAnalise = null;

            for (let i = 0; i < files.length; i++) {
                const currentFile = files[i];
                setBatchStatus(`Analisando arquivo IPE ${i + 1} de ${files.length}: ${currentFile.name}`);
                setBatchProgress(((i) / files.length) * 100);

                const formData = new FormData();
                formData.append('file', currentFile);
                formData.append('convenio_id', selectedConvenio);

                const response = await axios.post(`${flaskServer.flaskServerIP}/ipe/analisar`, formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                lastAnalise = response.data;
            }

            setAnaliseAtual(lastAnalise);
            setBatchProgress(100);
            setBatchStatus('Processamento concluído!');
            carregarHistorico();
        } catch (err) {
            setError('Erro ao analisar IPE: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleAprender = async () => {
        if (files.length === 0) return;
        if (!selectedConvenio || selectedConvenio === 'null') {
            setError('Selecione um convênio para o aprendizado IPE.');
            return;
        }

        setLoading(true);
        setBatchProgress(0);

        try {
            const token = localStorage.getItem('token');
            for (let i = 0; i < files.length; i++) {
                const currentFile = files[i];
                setBatchStatus(`Alimentando histórico IPE ${i + 1}/${files.length}: ${currentFile.name}`);
                setBatchProgress(((i) / files.length) * 100);

                const formData = new FormData();
                formData.append('file', currentFile);
                formData.append('convenio_id', selectedConvenio);

                await axios.post(`${flaskServer.flaskServerIP}/ipe/aprender`, formData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
            }
            setBatchStatus('Histórico alimentado com sucesso!');
        } catch (err) {
            setError('Erro no aprendizado IPE: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleFeedback = async (alertaIdx, acao) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${flaskServer.flaskServerIP}/ipe/feedback`, {
                guia: analiseAtual.guia,
                alerta_idx: alertaIdx,
                acao: acao
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const novosAlertas = [...analiseAtual.alertas];
            novosAlertas[alertaIdx].feedback = acao;
            setAnaliseAtual({ ...analiseAtual, alertas: novosAlertas });

        } catch (err) {
            alert('Erro ao processar feedback IPE');
        }
    };

    const getFilteredAlerts = () => {
        if (!analiseAtual || !analiseAtual.alertas) return [];
        return analiseAtual.alertas.filter(alerta => {
            const matchesType = filterType === 'Todos' || alerta.tipo === filterType;
            const matchesGuia = filterGuia === 'Todos' || alerta.guia === filterGuia;

            const term = filterItem.toLowerCase().trim();
            const matchesItem = !term ||
                (alerta.codigo && alerta.codigo.toLowerCase().includes(term)) ||
                (alerta.descricao && alerta.descricao.toLowerCase().includes(term));

            return matchesType && matchesGuia && matchesItem;
        });
    };

    const getScoreColor = (score) => {
        if (score < 30) return '#4caf50';
        if (score < 60) return '#ff9800';
        return '#f44336';
    };

    const filteredAlerts = getFilteredAlerts();
    const uniqueGuias = analiseAtual?.todas_guias || [];

    return (
        <Container maxWidth={viewMode === 'pivot' ? false : "lg"} sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
                <AssessmentIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                        Auditoria Inteligente Custom (IPE)
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Auditoria customizada para layouts proprietários (IpeWin)
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={4}>
                <Grid item xs={12} md={viewMode === 'pivot' ? 12 : 7}>
                    <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
                        <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                            <CloudUploadIcon sx={{ mr: 1 }} /> Novo Arquivo IPE
                        </Typography>

                        <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 4, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.02)', mb: 3 }}>
                            <input
                                accept=".035,.055,.075,.085"
                                style={{ display: 'none' }}
                                id="ipe-upload"
                                type="file"
                                multiple
                                onChange={handleFileChange}
                            />
                            <label htmlFor="ipe-upload">
                                <Button variant="contained" component="span" startIcon={<HospitalIcon />}>
                                    Selecionar Arquivos IPE
                                </Button>
                            </label>
                            {files.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" fontWeight="bold">{files.length} arquivo(s) selecionado(s)</Typography>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>Selecione o Convênio:</Typography>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                value={selectedConvenio}
                                onChange={(e) => setSelectedConvenio(e.target.value)}
                                SelectProps={{ native: true }}
                            >
                                <option value="null">-- Selecione um Convênio --</option>
                                {convenios.map((c) => (
                                    <option key={c.id} value={c.id}>{c.nome}</option>
                                ))}
                            </TextField>
                        </Box>

                        <Box display="flex" gap={2}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={handleAnalisar}
                                disabled={files.length === 0 || loading}
                                sx={{ py: 1.5 }}
                            >
                                {loading ? 'Processando...' : 'Executar Auditoria IPE'}
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={handleAprender}
                                disabled={files.length === 0 || loading || selectedConvenio === 'null'}
                            >
                                Alimentar Histórico
                            </Button>
                        </Box>

                        {loading && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="body2" color="primary" gutterBottom>{batchStatus}</Typography>
                                <LinearProgress variant="determinate" value={batchProgress} sx={{ height: 10, borderRadius: 5 }} />
                            </Box>
                        )}

                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                        {analiseAtual && (
                            <Box sx={{ mt: 4 }}>
                                <Divider sx={{ mb: 3 }} />
                                <Card variant="outlined" sx={{ mb: 3, borderLeft: `6px solid ${getScoreColor(analiseAtual.score)}` }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="textSecondary">SCORE DE RISCO IPE</Typography>
                                        <Typography variant="h3" fontWeight="bold" sx={{ color: getScoreColor(analiseAtual.score) }}>
                                            {analiseAtual.score}%
                                        </Typography>
                                    </CardContent>
                                </Card>

                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                    <TextField
                                        select
                                        size="small"
                                        label="Tipo de Alerta"
                                        value={filterType}
                                        onChange={(e) => setFilterType(e.target.value)}
                                        SelectProps={{ native: true }}
                                        fullWidth
                                    >
                                        <option value="Todos">Todos</option>
                                        <option value="Preço Atípico (IPE)">Preço Atípico</option>
                                        <option value="Preço Acima da tabela">Preço Acima da Tabela</option>
                                        <option value="Preço Abaixo da tabela">Preço Abaixo da Tabela</option>
                                        <option value="Possível Omissão (IPE)">Possível Omissão</option>
                                    </TextField>
                                    <TextField
                                        select
                                        size="small"
                                        label="Guia/Nota"
                                        value={filterGuia}
                                        onChange={(e) => setFilterGuia(e.target.value)}
                                        SelectProps={{ native: true }}
                                        fullWidth
                                    >
                                        <option value="Todos">Todas</option>
                                        {uniqueGuias.map(g => <option key={g} value={g}>{g}</option>)}
                                    </TextField>
                                    <TextField
                                        size="small"
                                        label="Filtrar Item (Cód/Desc)"
                                        value={filterItem}
                                        onChange={(e) => setFilterItem(e.target.value)}
                                        fullWidth
                                        placeholder="Ex: 99995..."
                                    />
                                </Box>

                                <Box display="flex" justifyContent="flex-end" mb={1}>
                                    <Button
                                        size="small"
                                        startIcon={viewMode === 'list' ? <TableChartIcon /> : <ListIcon />}
                                        onClick={() => setViewMode(viewMode === 'list' ? 'pivot' : 'list')}
                                    >
                                        {viewMode === 'list' ? 'Tabela Dinâmica' : 'Lista'}
                                    </Button>
                                </Box>

                                {viewMode === 'pivot' ? (
                                    <PivotTabelaProcessada dados={analiseAtual.alertas} />
                                ) : (
                                    <List>
                                        {filteredAlerts.length === 0 ? (
                                            <Typography variant="body2" sx={{ textAlign: 'center', py: 4 }}>Nenhum alerta encontrado.</Typography>
                                        ) : (
                                            filteredAlerts.map((alerta, idx) => (
                                                <Paper key={idx} sx={{ mb: 1, border: '1px solid #eee' }}>
                                                    <ListItem>
                                                        <ListItemIcon>
                                                            {alerta.nivel === 'Alto' ? <ErrorIcon color="error" /> : <WarningIcon color="warning" />}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={alerta.tipo}
                                                            secondary={`${alerta.descricao} | Guia: ${alerta.guia}`}
                                                        />
                                                        {alerta.feedback ? (
                                                            <Chip label={alerta.feedback} size="small" color={alerta.feedback === 'aceitar' ? 'success' : 'default'} />
                                                        ) : (
                                                            <Box>
                                                                <IconButton size="small" color="success" onClick={() => handleFeedback(idx, 'aceitar')}>
                                                                    <CheckCircleIcon />
                                                                </IconButton>
                                                                <IconButton size="small" color="error" onClick={() => handleFeedback(idx, 'rejeitar')}>
                                                                    <CloseIcon />
                                                                </IconButton>
                                                            </Box>
                                                        )}
                                                    </ListItem>
                                                </Paper>
                                            ))
                                        )}
                                    </List>
                                )}
                            </Box>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={5}>
                    <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom display="flex" alignItems="center">
                            <HistoryIcon sx={{ mr: 1 }} /> Histórico IPE
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <List>
                            {historico.length === 0 ? (
                                <Typography variant="body2" sx={{ textAlign: 'center', py: 4 }}>Nenhum histórico encontrado.</Typography>
                            ) : (
                                historico.map((item) => (
                                    <ListItem key={item.id} divider>
                                        <ListItemText
                                            primary={`Nota: ${item.guia}`}
                                            secondary={`${item.data} | Score: ${item.score}%`}
                                        />
                                        <IconButton size="small" color="primary" onClick={() => setAnaliseAtual(item)}>
                                            <TrendingUpIcon />
                                        </IconButton>
                                    </ListItem>
                                ))
                            )}
                        </List>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}

export default CustomAudit;
